import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiCall } from '@/utils/apiAxios';
import { Auxiliaries, Permission, Role } from '@/types/permissions/permissions';
import { useAgentStore } from '@/stores/agent';
import { catalogIsFresh, catalogTouch } from '@/utils/catalogCache';

function safeParseArray<T>(key: string): T[] {
    try {
        const raw = localStorage.getItem(key);
        if (!raw || raw === 'undefined' || raw === 'null') return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function safeParseObject<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw || raw === 'undefined' || raw === 'null') return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export const usePermitsStore = defineStore('permitsStore', () => {
    const permissions = ref<Permission[]>(safeParseArray<Permission>('permits'));
    const permissionsUser = ref<Permission[]>(safeParseArray<Permission>('permissionsUser'));
    const roles = ref<Role[]>([]);
    const auxiliaries = ref<Auxiliaries[]>(safeParseArray<Auxiliaries>('auxiliaries'));
    // Titular (agente dueño de la cuenta), aditivo al response de /brokerin/api/auxiliaries.
    // null si el backend aún no lo resuelve o no está desplegado — comportamiento intacto.
    const titular = ref<Auxiliaries | null>(safeParseObject<Auxiliaries>('auxiliaryTitular'));

    const agentStore = useAgentStore();

    // Nota (bug "Mi equipo" cierra sesión, 14-oct-2026): un 403 en estos
    // endpoints significa "no tienes permiso sobre ESE auxiliar/sección", NO
    // "tu apikey venció" (body real: {"status":0,"error":{"message":"No tienes
    // permisos para esta sesión"}}, nunca "apikey inválida"). `skipAuthRedirect:
    // true` evita que apiAxios limpie localStorage y redirija ante un 403 — y
    // apiAxios tampoco muestra su Swal genérico en ese camino (el llamador, la
    // vista "Mi equipo", pinta su propio panel bloqueado con el mensaje). Otros
    // errores (red, 500) de estos mismos endpoints SÍ siguen mostrando el Swal
    // normal, porque `showAlert` queda en su default. El error 403 se captura
    // aquí y se devuelve normalizado como `{ status: 0, error }` para que el
    // llamador decida cómo pintarlo, en vez de dejar una excepción sin
    // controlar. El resto del portal (apikey vencida) sigue cerrando sesión
    // igual: `skipAuthRedirect` es aditivo con default `false`.
    const getPermits = async (user_id = '') => {
        if (user_id === '' && permissions.value.length > 0 && catalogIsFresh('permits')) return;

        interface data {
            platform: 'brokerin';
            user_id?: string;
            agent_hash: string;
        }

        let data: data = {
            platform: 'brokerin',
            agent_hash: agentStore.agentHash
        };

        if (user_id != '') {
            data.user_id = user_id;
        }

        try {
            const { request } = await apiCall({
                method: 'get',
                url: `/permissions/api/permissions/permissions`,
                params: data,
                skipAuthRedirect: true
            });

            const response = await request;
            if (response.status == 1) {
                permissions.value = response.response.permissions;
                roles.value = response.response.roles ?? [];

                if (user_id == '') {
                    permissionsUser.value = response.response.permissions;
                    localStorage.setItem('permissionsUser', JSON.stringify(permissionsUser.value));
                    localStorage.setItem('permits', JSON.stringify(permissions.value));
                    catalogTouch('permits');
                }
            }
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar a este miembro' } };
        }
    };

    const toggleUserPermission = async (permission: object) => {
        try {
            const { request } = await apiCall({
                method: 'post',
                url: `/permissions/api/permissions/toggle_user_permission`,
                data: { ...permission, agent_hash: agentStore.agentHash },
                skipAuthRedirect: true
            });

            const response = await request;
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar a este miembro' } };
        }
    };

    const updateLabel = async (labels: object) => {
        try {
            const { request } = await apiCall({
                method: 'post',
                url: `/permissions/api/permissions/update_label`,
                data: { ...labels, agent_hash: agentStore.agentHash },
                skipAuthRedirect: true
            });

            const response = await request;
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar a este miembro' } };
        }
    };
    const createRole = async (role: object) => {
        try {
            const { request } = await apiCall({
                method: 'post',
                url: `/permissions/api/permissions/create_role`,
                data: { ...role, agent_hash: agentStore.agentHash },
                skipAuthRedirect: true
            });

            const response = await request;
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar a este miembro' } };
        }
    };

    const assignRole = async (userRoles: object) => {
        try {
            const { request } = await apiCall({
                method: 'post',
                url: `/permissions/api/permissions/assign_user_role`,
                data: { ...userRoles, agent_hash: agentStore.agentHash },
                skipAuthRedirect: true
            });

            const response = await request;
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar a este miembro' } };
        }
    };

    const removeRol = async (userRoles: object) => {
        try {
            const { request } = await apiCall({
                method: 'post',
                url: `/permissions/api/permissions/remove_user_role`,
                data: { ...userRoles, agent_hash: agentStore.agentHash },
                skipAuthRedirect: true
            });

            const response = await request;
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar a este miembro' } };
        }
    };

    const getAuxiliaries = async () => {
        if (auxiliaries.value.length > 0 && catalogIsFresh('auxiliaries')) return;

        try {
            const { request } = await apiCall({
                method: 'get',
                url: `/brokerin/api/auxiliaries`,

                params: { agent_hash: agentStore.agentHash },
                skipAuthRedirect: true
            });

            const response = await request;

            if (response.status == 1) {
                auxiliaries.value = response.response;
                // Aditivo: titular puede venir undefined (backend viejo/no desplegado) o null
                // (agent_hash no resolvió user) — en ambos casos queda null, sin errores.
                titular.value = response.titular ?? null;

                localStorage.setItem('auxiliaries', JSON.stringify(auxiliaries.value));
                localStorage.setItem('auxiliaryTitular', JSON.stringify(titular.value));
                catalogTouch('auxiliaries');
            }
            return response;
        } catch (error: any) {
            return { status: 0, error: error?.response?.data?.error ?? { message: 'No tienes permisos para administrar al equipo' } };
        }
    };

    const getAuxiliariesInfo = (auxiliariesId: string) => {
        let assistant = auxiliaries.value.find((i) => i.id == auxiliariesId);
        if (assistant) {
            return assistant;
        } else {
            return { value: '0', label: 'Sin datos', friendly_name: 'Sin datos', color: 'gray' };
        }
    };

    return {
        permissions,
        roles,
        auxiliaries,
        titular,
        permissionsUser,
        getPermits,
        toggleUserPermission,
        updateLabel,
        createRole,
        assignRole,
        removeRol,
        getAuxiliaries,
        getAuxiliariesInfo
    };
});
